import { ReactNode } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { renderEmojiOrIcon } from './Icons';
import { EmojiPicker } from './EmojiPicker';

export interface DestinationStripSide {
  name: string;
  emoji: string;
  /** A DEST_GRADIENTS entry (or any valid CSS `background` value) for this side. */
  background: string;
}

// The Day Planner's destination-identity strip — a thin replacement for
// DestinationHeroCard (which stays as-is for Shortlists/DayDetail). One
// entry in `sides` is an ordinary day, showing that destination's emoji,
// name and weather. Two entries is a transit day (departing, arriving),
// rendered as two colour-matched halves that mirror around the centre
// arrow — departing hugs the left edge, arriving hugs the right — each
// showing just their destination's emoji + name (no weather on a transit
// day, since splitting the strip already leaves half the width per label).
export function DestinationStrip({
  sides, weatherSlot, hasPrev, hasNext, onNavigateDest,
  canEditEmoji, editingEmoji, onEditEmoji, onSelectEmoji, onCloseEmojiPicker,
}: {
  sides: DestinationStripSide[];
  weatherSlot?: ReactNode;
  hasPrev?: boolean;
  hasNext?: boolean;
  onNavigateDest?: (dir: 'prev' | 'next') => void;
  /** Only meaningful when sides.length === 1 — no single unambiguous
   *  destination to edit on a transit day. */
  canEditEmoji?: boolean;
  editingEmoji?: boolean;
  onEditEmoji?: () => void;
  onSelectEmoji?: (emoji: string) => void;
  onCloseEmojiPicker?: () => void;
}) {
  const isTransit = sides.length > 1;

  return (
    <motion.div
      drag={(hasPrev || hasNext) ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.15}
      onDragEnd={(_, info) => {
        if ((info.offset.x < -60 || info.velocity.x < -500) && hasNext) onNavigateDest?.('next');
        else if ((info.offset.x > 60 || info.velocity.x > 500) && hasPrev) onNavigateDest?.('prev');
      }}
      // Top corners only — this sits flush above ScheduleView's day-header
      // block (see DestinationScheduler.tsx) with zero gap, reading as one
      // joined heading rather than two stacked cards. No border or shadow
      // of its own: a border on the strip alone inset its visible colour by
      // a hairline on every edge (border-box), making it read as narrower
      // than the borderless header directly below — and a shadow here would
      // cut across the middle of the joined shape instead of sitting under
      // the whole thing.
      className={`relative flex rounded-t-2xl overflow-hidden touch-pan-y ${(hasPrev || hasNext) ? 'cursor-grab active:cursor-grabbing' : ''}`}
    >
      {sides.map((side, i) => {
        const isArriving = isTransit && i === sides.length - 1;
        const emoji = (
          <span className="text-xl leading-none shrink-0 select-none relative">
            {canEditEmoji && !isTransit ? (
              <button aria-label="Change destination emoji"
                onClick={onEditEmoji}
                className="cursor-pointer hover:scale-110 transition-transform active:scale-95"
              >
                {renderEmojiOrIcon(side.emoji, 22)}
              </button>
            ) : renderEmojiOrIcon(side.emoji, 22)}
            <AnimatePresence>
              {editingEmoji && !isTransit && onSelectEmoji && onCloseEmojiPicker && (
                <EmojiPicker currentEmoji={side.emoji} onSelect={onSelectEmoji} onClose={onCloseEmojiPicker} />
              )}
            </AnimatePresence>
          </span>
        );
        const label = <span className="text-white font-bold text-sm truncate drop-shadow-sm">{side.name}</span>;
        return (
          <div
            key={i}
            className={`relative flex-1 min-w-0 flex items-center gap-2.5 px-4 py-3.5 ${isArriving ? 'justify-end' : ''}`}
            style={{ background: side.background }}
          >
            <div className="absolute inset-0 bg-black/15 pointer-events-none" />
            <div className="relative z-10 flex items-center gap-2 min-w-0">
              {/* Arriving side mirrors the departing side: label then emoji,
                  hugging the right edge. */}
              {isArriving ? <>{label}{emoji}</> : <>{emoji}{label}</>}
            </div>
            {!isTransit && weatherSlot && (
              <div className="relative z-10 flex items-center gap-3 text-white/85 text-[10px] font-bold uppercase tracking-wider shrink-0 ml-auto pl-2">
                {weatherSlot}
              </div>
            )}
          </div>
        );
      })}
      {isTransit && (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center">
          <ArrowRight className="w-3.5 h-3.5 text-app-text" strokeWidth={2.5} />
        </div>
      )}
    </motion.div>
  );
}
